import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider
} from '@mui/material';
import {
  AccountCircle as AccountIcon,
  ExitToApp as LogoutIcon,
  Home as HomeIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { AdminDashboard } from './AdminDashboard';
import { AuthService } from '../services/auth';

export const AdminDashboardWrapper: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      console.log('AdminDashboardWrapper: Checking admin access...');
      
      // First check localStorage directly (since debug page works)
      const storedUser = localStorage.getItem('authUser');
      const storedToken = localStorage.getItem('authToken');
      
      console.log('AdminDashboardWrapper: Stored user:', storedUser);
      console.log('AdminDashboardWrapper: Has token:', !!storedToken);
      
      if (storedUser && storedToken) {
        try {
          const user = JSON.parse(storedUser);
          console.log('AdminDashboardWrapper: Parsed user:', user);
          console.log('AdminDashboardWrapper: User groups:', user.groups);
          
          if (user.groups?.includes('admins')) {
            console.log('AdminDashboardWrapper: Admin access granted via localStorage');
            setUser(user);
            setLoading(false);
            return;
          }
        } catch (parseError) {
          console.error('AdminDashboardWrapper: Error parsing stored user:', parseError);
        }
      }
      
      // Fallback to AuthService
      console.log('AdminDashboardWrapper: Trying AuthService...');
      const currentUser = await AuthService.getCurrentUser();
      console.log('AdminDashboardWrapper: AuthService user:', currentUser);
      
      if (!currentUser) {
        console.log('AdminDashboardWrapper: No current user, redirecting to login');
        navigate('/admin/login');
        return;
      }

      console.log('AdminDashboardWrapper: User groups:', currentUser.groups);
      console.log('AdminDashboardWrapper: User role:', currentUser.role);
      
      // Check if user has admin privileges (check for both 'admin' and 'admins')
      const hasAdminAccess = currentUser.groups?.includes('admin') || currentUser.groups?.includes('admins') || currentUser.role === 'admin';
      if (!hasAdminAccess) {
        console.log('AdminDashboardWrapper: User does not have admin privileges');
        setError('Access denied. Admin privileges required.');
        setTimeout(() => {
          navigate('/admin/login');
        }, 3000);
        return;
      }

      console.log('AdminDashboardWrapper: Admin access granted via AuthService');
      setUser(currentUser);
    } catch (err) {
      console.error('AdminDashboardWrapper: Error checking admin access:', err);
      navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await AuthService.logout();
      navigate('/admin/login');
    } catch (err) {
      console.error('Error signing out:', err);
      // Force navigation even if logout fails
      navigate('/admin/login');
    }
    handleMenuClose();
  };

  const handleGoHome = () => {
    navigate('/');
    handleMenuClose();
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Verifying admin access...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        gap={2}
        p={3}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Redirecting to login...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Admin Navigation Bar */}
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <AdminIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Birthday Party Admin Portal
          </Typography>
          
          {/* User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {user?.email}
            </Typography>
            <IconButton
              size="large"
              aria-label="account menu"
              aria-controls="admin-menu"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)' }}>
                <AccountIcon />
              </Avatar>
            </IconButton>
          </Box>

          <Menu
            id="admin-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {user?.email}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleGoHome}>
              <HomeIcon sx={{ mr: 1 }} />
              Guest Portal
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 1 }} />
              Sign Out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Admin Dashboard Content */}
      <AdminDashboard />
    </>
  );
};